'use client';

import { useCallback, useState } from 'react';
import { VoiceLibraryPage, type VoiceItem, getLibraryVoices } from '@/contexts/stories';
import { VoiceCloneWizard, deleteCustomVoice as deleteCustomVoiceAction } from '@/contexts/ai';
import { useEventSourceEvent } from '@/shared/hooks/useEventSourceEvent';

interface VoiceLibraryClientProps {
  locale: string;
  familyId?: string;
}

export function VoiceLibraryClient({ locale, familyId }: VoiceLibraryClientProps) {
  const [cloneWizardOpen, setCloneWizardOpen] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  const fetchVoices = useCallback(async (): Promise<VoiceItem[]> => {
    const voices = await getLibraryVoices();
    return voices as VoiceItem[];
  }, []);

  const deleteVoice = useCallback(
    async (voiceId: string): Promise<{ success: boolean }> => {
      if (!familyId) return { success: false };

      const result = await deleteCustomVoiceAction(voiceId);
      return { success: result.success };
    },
    [familyId]
  );

  const handleCloneVoice = useCallback(() => {
    setCloneWizardOpen(true);
  }, []);

  // SSE: auto-refresh voice list on clone completed or voice deleted.
  // The wizard stays open — it shows the preview + quality hints, the user closes it.
  useEventSourceEvent('voice.clone.completed', useCallback(() => {
    setRefreshKey((k) => k + 1);
  }, []));

  useEventSourceEvent('voice.deleted', useCallback(() => {
    setRefreshKey((k) => k + 1);
  }, []));

  return (
    <>
      <VoiceLibraryPage
        key={refreshKey}
        fetchVoices={fetchVoices}
        deleteVoice={deleteVoice}
        onCloneVoice={handleCloneVoice}
      />
      {familyId && (
        <VoiceCloneWizard
          open={cloneWizardOpen}
          onOpenChange={setCloneWizardOpen}
          familyId={familyId}
          onEnqueued={() => {
            // Wizard stays open: spinner → preview once the SSE confirms completion
          }}
        />
      )}
    </>
  );
}
